import { useEffect, useRef } from "react";
import { useNavigation } from "react-router";

import { pushEvent } from "~/hec/analytics/event-layer/gtm-sink";
import { trackAbTastyEvent } from "~/lib/track-abtasty-event.client";

/**
 * Emits a per-SPA-navigation timing event (SFCC-11889 C7) — the soft-nav
 * replacement for `page_load_time`, which GTM derives from Navigation Timing
 * and therefore only produces on full page loads (~0.5× of route views once
 * most navigation is client-side).
 *
 * Measures from the first non-idle `useNavigation()` tick (link click /
 * form navigation — fetchers don't register here) to the effect after the
 * router returns to idle, i.e. loaders resolved + new route committed. Pushes
 * `GA4_routeChange` with `route_change_time_ms`, and drops a
 * `performance.mark` for devtools/RUM correlation.
 *
 * Net-new event, fork-gated and dormant until martech adds the container
 * trigger — the `GA4_search` coordination path. Hard loads intentionally push
 * nothing: GTM's existing page_load_time covers those.
 */
export function RouteChangePerfTracker() {
  const navigation = useNavigation();
  const startedAt = useRef<number | null>(null);

  useEffect(() => {
    if (navigation.state !== "idle") {
      // First non-idle tick of this navigation only — a loading→submitting
      // flip mid-navigation must not reset the clock.
      if (startedAt.current === null) startedAt.current = performance.now();
      return;
    }
    if (startedAt.current === null) return;
    const duration = Math.round(performance.now() - startedAt.current);
    startedAt.current = null;
    performance.mark?.("hec:route-change");
    pushEvent({ event: "GA4_routeChange", route_change_time_ms: duration });
    // AB Tasty Universal Collect EVENT hit (SFCC-12079). Route-change half of
    // the "page scroll / route change" -> page_engagement row; scroll-depth
    // tracking has no existing tracker in this codebase to hook into yet.
    trackAbTastyEvent({ ec: "User Engagement", ea: "page_engagement" });
  }, [navigation.state]);

  return null;
}
